import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs/Rx';

import { Attack, AttackParams, AttackProfile } from '../../attack/attack';
import { AttackService } from '../../attack/attack.service';
import { AwsEnvironment } from '../../aws/environment';
import {ConfigService} from '../config/config.service';
import { TargetValidationService } from '../../main/target-validation/target-validation.service';
import { TargetValidationSubscription } from '../../main/target-validation/target-validation-subscription';
import {SystemInformationService} from '../../systeminformation/systeminformation.service';
import { isNullOrUndefined } from 'util';
import {TrialService} from '../../trial/trial.service';

class AttackConfig {
    id?: string;
    target: Target;
    ddosProfile: string;
    throughput: number;
    volume: string;
    duration: number;
    region: string;
}

class Target {
    host: Host;
    port: Port;
}

class Host {
    ip?: string;
    name?: string;
}

class Port {
    number?: number;
}


@Component({
    selector: 'ddos-attack-config',
    templateUrl: './ddos-attack-config.component.html',
    styleUrls: [
        './ddos-attack-config.component.scss',
        '../../../../node_modules/bootstrap/dist/css/bootstrap.css'
    ]
})
export class DdosAttackConfigComponent implements OnInit, OnDestroy {
    @ViewChild('attackForm') attackForm: NgForm;

    availableAttackProfiles: AttackProfile[] = [];
    attackConfig: AttackConfig;
    currentAttack: Attack;
    canStartAttack: boolean = true;
    canStopAttack: boolean = false;
    startStopOperationInProgress: boolean;
    error: string;
    disableInputs: boolean = false;

    showDevDurations: boolean = true;


    selectedAttackSizes: any[];
    showEstimatedTime: boolean = false;
    estimatedTime: string = '15';
    showEstimatedOutboundData: boolean = true;
    estimatedOutboundData: string;

    private currentAttackSubscription: Subscription;

    constructor(
        private attackService: AttackService,
        private awsEnv: AwsEnvironment,
        private cfgService: ConfigService,
        private targetValidationService: TargetValidationService,
        private systemInformationService: SystemInformationService,
        private trialService: TrialService
    ) { }

    public ngOnInit(): void {

        this.systemInformationService.getSystemInformation().subscribe(information => {
            if(information['ShowDevDurations'] != null){
                this.showDevDurations = information['ShowDevDurations']
            }
        });

        this.updateAttackConfig();


        this.cfgService.availableAttackProfilesSubject
            .subscribe(
                (profiles) =>{
                    profiles.sort((as, bs) => {
                        var a: any, b: any, a1: number, b1: number, i= 0, n: number , L,
                            rx=/(\.\d+)|(\d+(\.\d+)?)|([^\d.]+)|(\.\D+)|(\.$)/g;
                        if(as.Label=== bs.Label) return 0;
                        a= as.Label.toLowerCase().match(rx);
                        b= bs.Label.toLowerCase().match(rx);
                        L= a.length;
                        while(i<L){
                            if(!b[i]) return 1;
                            a1= a[i],
                                b1= b[i++];
                            if(a1!== b1){
                                n= a1-b1;
                                if(!isNaN(n)) return n;
                                return a1>b1? 1:-1;
                            }
                        }
                        return b[i]? -1:0;
                    });

                    this.availableAttackProfiles = profiles;
                    this.updateAttackConfig();
                }
            );


        this.currentAttackSubscription = this.cfgService.currentAttackSubject
            .subscribe(attack => {
                if(attack){
                    this.currentAttack = attack;
                    //console.log(attack);

                    this.canStartAttack = !attack || ['Stopped', 'Failed', 'Unknown'].indexOf(attack.Status) >= 0;
                    this.canStopAttack = attack && ['Queued', 'Preparing', 'Started'].indexOf(attack.Status) >= 0;

                    if(attack.Status === 'Queued' || attack.Status === 'Preparing' ||  attack.Status === 'Starting' || attack.Status === 'Stopping' || attack.Status === 'Started'){
                        this.disableInputs = true;

                    }else if(attack.Status === 'Stopped' || attack.Status === 'Failed') {
                        this.disableInputs = false;
                    }
                    else{
                        this.disableInputs = false;
                    }

                    this.updateAttackConfig();
                }
            });
    }

    ngOnDestroy() {
        this.currentAttackSubscription.unsubscribe();
    }

    updateAttackConfig() {
        if (this.currentAttack) {
            this.attackConfig = {
                target: {
                    host: {
                        ip: this.currentAttack.AttackParams.TargetIpAddress
                    },
                    port: {
                        number: this.currentAttack.AttackParams.TargetPortNumber
                    }
                },
                ddosProfile: this.currentAttack.AttackParams.AttackProfile,
                throughput: 1000,
                duration: this.currentAttack.AttackParams.Duration,
                volume: this.currentAttack.AttackParams.AttackSize,
                region: this.currentAttack.AttackParams.SourceRegionName
            };

            if(this.attackConfig.region === ''){
                this.attackConfig.region = this.awsEnv.getDefaultSourceRegion();
            }
        } else {
            let defaultProfile = this.getDefaultAttackProfile();
            this.attackConfig = {
                target: {
                    host: {
                        ip: ''
                    },
                    port: {
                        number: 80
                    }
                },
                ddosProfile: defaultProfile ? defaultProfile.Name : null,
                throughput: 1000,
                duration: 600,
                volume: 'small',
                region: this.awsEnv.getDefaultSourceRegion()
            };
        }

        let profileIndex = this.findWithAttr(this.availableAttackProfiles, 'Name', this.attackConfig.ddosProfile);
        if (this.attackConfig.ddosProfile && this.availableAttackProfiles.length && (profileIndex !== -1)) {
            this.selectedAttackSizes = this.availableAttackProfiles[profileIndex].Sizes;
        }
        this.calculateEstimatedThroughput();
    }

    findWithAttr(array: any, attr: string, value: string) {
        for (var i = 0; i < array.length; i += 1) {
            if (array[i][attr] === value) {
                return i;
            }
        }
        return -1;
    }

    getDefaultAttackProfile(): AttackProfile {
        if (!this.availableAttackProfiles) {
            return null;
        }
        for (var profile of this.availableAttackProfiles) {
            if (profile.Default) {
                return profile;
            }
        }
        return this.availableAttackProfiles[0] || null;
    }

    getAttackParams(): AttackParams {
        return {
            TargetIpAddress: this.attackConfig.target.host.ip,
            TargetPortNumber: this.attackConfig.target.port.number,
            AttackProfile: this.attackConfig.ddosProfile,
            AttackSize: this.attackConfig.volume,
            Duration: this.attackConfig.duration,
            SourceRegionName: this.attackConfig.region
        };
    }

    onStartStopAttack() {
        this.startStopOperationInProgress = true;
        this.error = null;
        if (this.canStartAttack) {
            if ( this.awsEnv.isIpValidationEnabled()) {
                this.targetValidationService.getSubscriptions().subscribe(
                    (subcriptions : TargetValidationSubscription[]) => {
                        var validSubscription = false;
                        subcriptions.forEach(subscription => {
                            if (subscription.valid) {
                                validSubscription = true;
                            }
                        });
                        if (!validSubscription) {
                            this.targetValidationService.showDialog();
                            this.startStopOperationInProgress = false;
                        }
                        else {
                            this.startAttack();
                        }
                    },
                    (error: string) => {
                        this.error = error;
                        this.startStopOperationInProgress = false;
                        console.log('Error during start test: ' + error);
                    }
                );
            } else {
                // no ip validation
                this.startAttack();
            }


        } else if (this.canStopAttack) {
            this.stopAttack();
        }
    }

    onAttackSelect(event: any) {
        //console.log(event);

        this.selectedAttackSizes = this.availableAttackProfiles[event.target.selectedIndex].Sizes;
        const valueFound = this.findWithAttr(this.selectedAttackSizes, 'Name',this.attackConfig.volume);
        if(valueFound === -1){
            this.attackConfig.volume = this.selectedAttackSizes[0].Name; //If value doesn't exist revert to 0th
        }

        this.calculateEstimatedThroughput();
    }


    startAttack() {
        // fix the source region
        this.attackConfig.region = this.awsEnv.getDefaultSourceRegion();

        this.attackService.start(this.getAttackParams()).subscribe(
            (attack: Attack) => {
                this.startStopOperationInProgress = false;
                //console.log(attack);
            },
            (error: string) => {
                this.error = error;
                this.startStopOperationInProgress = false;
                console.log('Error during start test: ' + error);
            },
            () => {
            });
    }

    stopAttack() {
        this.attackService.stop(this.currentAttack.ID).subscribe(
            (attack: Attack) => {
                this.startStopOperationInProgress = false;
                //console.log(attack);
            },
            (error: string) => {
                this.error = error;
                this.startStopOperationInProgress = false;
                console.log('Error during stop test: ' + error);
            },
            () => {
            });
    }

    onDurationChange() {
        this.calculateEstimatedThroughput();
    }

    onAttackSizeSelect(event: any) {
        this.calculateEstimatedThroughput();

    }


    bytesToSize(size: number) : string {
        var i = Math.floor( Math.log(size) / Math.log(1000) );
        if(size === 0){
            return '0 MB';
        }
        return +( size / Math.pow(1000, i) ).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
    };

    calculateEstimatedThroughput() {
        if(this.selectedAttackSizes && this.attackConfig.volume && this.attackConfig.duration){
            const idx = this.findWithAttr(this.selectedAttackSizes, 'Name', this.attackConfig.volume);
            if(idx > -1){
                const bytes = this.selectedAttackSizes[idx].max * 1000 * 1000  / 8; //max is in bits
                this.estimatedOutboundData = this.bytesToSize(bytes * this.attackConfig.duration);
                this.trialService.setEstimatedOutboundData(parseFloat((( (bytes * this.attackConfig.duration )  / 1000000) / 1000).toFixed(2)));
                this.showEstimatedOutboundData = true;
            }else{
                //Something went wrong. don't show estimated outbound data
                this.showEstimatedOutboundData = false;
            }

        }
    }
}



// WEBPACK FOOTER //
// ./src/app/main/ddos-attack-config/ddos-attack-config.component.ts